"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AgentVStreamEventTerminalTypes = exports.AgentVStreamEventTypeSchema = exports.AgentVStreamEventTypeIterable = exports.AgentVStreamEventSchema = exports.AgentVErrorEventSchema = exports.AgentVTurnEndEventSchema = exports.AgentVHeartbeatEventSchema = exports.AgentVBlockEndEventSchema = exports.AgentVReasoningDeltaEventSchema = exports.AgentVTextDeltaEventSchema = exports.AgentVBlockStartEventSchema = exports.AgentVTurnStartEventSchema = exports.AgentVBlockTypeSchema = exports.AgentVBlockTypeIterable = exports.AgentVBlockSchema = exports.AgentVGraphDiffBlockSchema = exports.AgentVToolCallBlockSchema = exports.AgentVReasoningBlockSchema = exports.AgentVTextBlockSchema = exports.AgentVTurnRequestSchema = exports.AgentVHistoryTurnSchema = void 0;
const zod_1 = require("zod");
/**
 * ************************************
 * AGENTV TYPES
 * ************************************
 *
 * Shared front/back contract for the in-app Backup Servicing (BUS) agent — the
 * PostHog-"Max"-style assistant that runs in-process in finleycore and streams
 * to the admin (and eventually core) dashboard chat window. See
 * `docs/bus/reference/agentv-design.md`.
 *
 * This file defines the WIRE CONTRACT only. It is deliberately split into two
 * independent discriminated unions, because they answer two different questions:
 *
 *   (1) AgentVBlock       — the addressable, reconstructed unit the transcript
 *                             RENDERS. Each block carries a SPEC, never rendered
 *                             HTML (mirrors PostHog Max's `VisualizationMessage`,
 *                             which carries a query the product's own component
 *                             renders). The client keeps an ordered array of
 *                             these and hands each to the matching component.
 *
 *   (2) AgentVStreamEvent — the ephemeral, ordered SSE frames sent over the
 *                             wire during a single turn. They are NOT persisted;
 *                             the client reduces them into a AgentVBlock[]
 *                             transcript. Mirrors the Anthropic SDK stream shape.
 *
 * Sequencing note (step 1): the stream pipeline is exercised end-to-end, but
 * only the `text` and `reasoning` blocks render live. `toolCall` and `graphDiff`
 * are reserved in the union now (shapes first) and rendered when the v2
 * graph-editing tools land — see `docs/bus/reference/agentv-design.md` §7.
 *
 * Persistence note: step 1 is stateless (no thread table). When threads are
 * persisted, add wire/domain date variants (string vs Date) exactly as
 * `FinleyAi.ts` does for its prompt-response schemas.
 */
/**
 * One prior turn, replayed by the client so the model has conversational
 * context. AgentV has no server-side session (it is stateless — see the
 * persistence note above), and the client already holds the full transcript, so
 * history travels on the request rather than being loaded server-side.
 *
 * Text-only by design: `answer` is the assistant's concatenated `text` blocks
 * for that turn. Reasoning and tool activity are intentionally dropped — the
 * facts a follow-up needs ("is that correct?") live in the assistant text, and
 * the model re-runs tools if it needs fresh data. This keeps the replay a simple
 * alternating user/assistant transcript with no fragile tool_use/tool_result
 * reconstruction.
 */
exports.AgentVHistoryTurnSchema = zod_1.z.object({
    // Reuses the same bound as `prompt` below — a replayed prompt was itself once
    // a live prompt, so the same cap applies.
    prompt: zod_1.z.string().min(1).max(10_000),
    // The assistant's text answer. May be empty when a turn produced only
    // reasoning/tool activity and no final text; the loop substitutes a
    // placeholder so message-role alternation and the non-empty-content
    // requirement both hold.
    answer: zod_1.z.string().max(50_000),
});
/**
 * ************************************
 * TURN REQUEST (client → server)
 * ************************************
 * What the client POSTs to open or continue a turn. `targetOrganizationId` is
 * the selected BUS org and is the enforcement point for containment boundary #2
 * (data boundary) — every tool hard-filters to it. Mirrors the shape of
 * `AbstractFinleyAiRequest` in `FinleyAi.ts`.
 */
exports.AgentVTurnRequestSchema = zod_1.z.object({
    threadId: zod_1.z.string().nullable(), // null = start a new thread
    targetOrganizationId: zod_1.z.string().uuid(), // the selected BUS org (containment boundary #2)
    // Non-empty and bounded: rejects blank turns and caps input before it reaches
    // the model, so an unbounded prompt can't bypass cost/rate controls.
    prompt: zod_1.z.string().min(1).max(10_000),
    // Prior turns replayed for context, oldest first, NOT including the current
    // `prompt`. Bounded to cap the context (and cost) a single turn can carry;
    // defaults to empty so an omitted field starts a fresh conversation.
    history: zod_1.z.array(exports.AgentVHistoryTurnSchema).max(50).default([]),
});
/**
 * ************************************
 * (1) RENDERED CONTENT BLOCKS
 * ************************************
 * The addressable transcript unit. Discriminated on `type`. Each variant
 * carries a spec, not rendered markup.
 */
/** Plain assistant text; rendered as markdown. */
exports.AgentVTextBlockSchema = zod_1.z.object({
    type: zod_1.z.literal('text'),
    text: zod_1.z.string(),
});
/**
 * Summarized reasoning ("thinking") display. We stream `display: 'summarized'`
 * from the model, so this is a summary string, never raw chain-of-thought.
 */
exports.AgentVReasoningBlockSchema = zod_1.z.object({
    type: zod_1.z.literal('reasoning'),
    summary: zod_1.z.string(),
});
/**
 * A single tool call the agent made, plus its (summarized) result. `input` is
 * the model's tool input; `resultSummary` is the AI-readable formatter string
 * returned to the model, NOT raw controller JSON (see design doc §4.2).
 *
 * RESERVED: rendered once the v1 read tools land. Present now so the transcript
 * reducer already knows how to place tool activity in order.
 */
exports.AgentVToolCallBlockSchema = zod_1.z.object({
    type: zod_1.z.literal('toolCall'),
    toolCallId: zod_1.z.string(),
    toolName: zod_1.z.string(),
    input: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    status: zod_1.z.enum(['pending', 'success', 'error']),
    resultSummary: zod_1.z.string().nullable(),
});
/**
 * A proposed tape-builder graph change, rendered as a diff with an approval
 * gate. `before`/`after` are `artifactsDocumentString`s — the exact wire shape
 * the existing `saveBorrowingBasePipelineArtifactDocumentWip` mutation uses — so
 * the UI can hand them straight to the existing tape-builder `DiffViewer` with
 * zero translation (PostHog's "spec → existing component" pattern).
 *
 * RESERVED for v2 (graph mutations). Defined now so the approval-gate flow is
 * a matter of rendering an already-agreed shape, not a new contract. See design
 * doc §7 (dangerous ops behind human approval).
 */
exports.AgentVGraphDiffBlockSchema = zod_1.z.object({
    type: zod_1.z.literal('graphDiff'),
    pipelineDefinitionId: zod_1.z.string(),
    before: zod_1.z.string(), // artifactsDocumentString (existing tape-builder wire shape)
    after: zod_1.z.string(), // artifactsDocumentString
    approvalState: zod_1.z.enum(['proposed', 'approved', 'rejected', 'committed']),
});
// Discriminated on `type`: O(1) dispatch and clearer errors than z.union's
// linear candidate-matching — every variant already carries a `type` literal.
exports.AgentVBlockSchema = zod_1.z.discriminatedUnion('type', [
    exports.AgentVTextBlockSchema,
    exports.AgentVReasoningBlockSchema,
    exports.AgentVToolCallBlockSchema,
    exports.AgentVGraphDiffBlockSchema,
]);
/** Discriminant values for `AgentVBlock`, for exhaustive client switches. */
exports.AgentVBlockTypeIterable = [
    'text',
    'reasoning',
    'toolCall',
    'graphDiff',
];
exports.AgentVBlockTypeSchema = zod_1.z.enum(exports.AgentVBlockTypeIterable);
/**
 * ************************************
 * (2) STREAM EVENTS (server → client, SSE)
 * ************************************
 * Ephemeral, ordered frames for one turn. The client reduces these into a
 * `AgentVBlock[]`: `blockStart` appends the block at `index`, the `*Delta`
 * events patch the block at `index`, and `blockEnd` finalizes it.
 */
/** Turn accepted; carries the (new or existing) thread id the turn belongs to. */
exports.AgentVTurnStartEventSchema = zod_1.z.object({
    type: zod_1.z.literal('turnStart'),
    threadId: zod_1.z.string(),
    // Bedrock model id serving this turn, so the UI can display which model is
    // in use rather than hardcoding it.
    model: zod_1.z.string(),
});
/** A new block opened at `index`; `block` is its initial (possibly empty) state. */
exports.AgentVBlockStartEventSchema = zod_1.z.object({
    type: zod_1.z.literal('blockStart'),
    index: zod_1.z.number().int(),
    block: exports.AgentVBlockSchema,
});
/** Append `delta` to the text of the `text` block at `index`. */
exports.AgentVTextDeltaEventSchema = zod_1.z.object({
    type: zod_1.z.literal('textDelta'),
    index: zod_1.z.number().int(),
    delta: zod_1.z.string(),
});
/** Append `delta` to the summary of the `reasoning` block at `index`. */
exports.AgentVReasoningDeltaEventSchema = zod_1.z.object({
    type: zod_1.z.literal('reasoningDelta'),
    index: zod_1.z.number().int(),
    delta: zod_1.z.string(),
});
/** Finalize the block at `index`; `block` is its complete, validated state. */
exports.AgentVBlockEndEventSchema = zod_1.z.object({
    type: zod_1.z.literal('blockEnd'),
    index: zod_1.z.number().int(),
    block: exports.AgentVBlockSchema,
});
/**
 * Keep-alive. Emitted on an interval while the model thinks so the byte gap
 * stays under the SSE/CloudFront origin-read timeout — this is what makes the
 * same stream reusable from the core dashboard (behind CloudFront) later, and
 * stops the UI looking hung during long reasoning.
 */
exports.AgentVHeartbeatEventSchema = zod_1.z.object({
    type: zod_1.z.literal('heartbeat'),
});
/** Turn complete. No more events for this turn will follow. */
exports.AgentVTurnEndEventSchema = zod_1.z.object({
    type: zod_1.z.literal('turnEnd'),
    threadId: zod_1.z.string(),
});
/** Terminal error; the turn is aborted. `message` is user-safe. */
exports.AgentVErrorEventSchema = zod_1.z.object({
    type: zod_1.z.literal('error'),
    message: zod_1.z.string(),
});
// Discriminated on `type`: parsed on the SSE hot path (every frame), so O(1)
// dispatch on the discriminant beats z.union's linear matching.
exports.AgentVStreamEventSchema = zod_1.z.discriminatedUnion('type', [
    exports.AgentVTurnStartEventSchema,
    exports.AgentVBlockStartEventSchema,
    exports.AgentVTextDeltaEventSchema,
    exports.AgentVReasoningDeltaEventSchema,
    exports.AgentVBlockEndEventSchema,
    exports.AgentVHeartbeatEventSchema,
    exports.AgentVTurnEndEventSchema,
    exports.AgentVErrorEventSchema,
]);
/** Discriminant values for `AgentVStreamEvent`. */
exports.AgentVStreamEventTypeIterable = [
    'turnStart',
    'blockStart',
    'textDelta',
    'reasoningDelta',
    'blockEnd',
    'heartbeat',
    'turnEnd',
    'error',
];
exports.AgentVStreamEventTypeSchema = zod_1.z.enum(exports.AgentVStreamEventTypeIterable);
/** Stream-event types after which no further events arrive for a turn. */
exports.AgentVStreamEventTerminalTypes = [
    'turnEnd',
    'error',
];
//# sourceMappingURL=AgentV.js.map